import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type { Organization } from '@/types/organization';
import { getOrganizationList, peekOrganizationList, subscribeOrganizationList } from './organizationList';
import { getAuthGeneration, getSessionSubject, subscribeSessionIdentity } from './token';

export const useOrganizationList = (): {
  organizations: Organization[] | null;
  loading: boolean;
  error: Error | null;
  retry: () => void;
  retrying: boolean;
} => {
  const publishedOrganizations = useSyncExternalStore(subscribeOrganizationList, peekOrganizationList, () => null);
  const [fallback, setFallback] = useState<{ organizations: Organization[]; subject: string } | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const authGeneration = useSyncExternalStore(subscribeSessionIdentity, getAuthGeneration, getAuthGeneration);

  const retry = useCallback(() => {
    setRetrying(true);
    setRetryNonce((value) => value + 1);
  }, []);

  // Depend on availability, not the array itself: every publish writes a new
  // array identity, so an array dependency would re-run this effect (and
  // re-set the fallback) on each background refresh.
  const hasPublishedList = publishedOrganizations !== null;

  useEffect(() => {
    let mounted = true;
    const published = hasPublishedList ? peekOrganizationList() : null;
    if (published) {
      const publishedSubject = getSessionSubject();
      // Another consumer's fetch may have populated the list after this hook
      // recorded a failure; a stale error must not keep the UI in error state.
      // Retaining the published list in the fallback also carries it across
      // the store's TTL boundary, so expiry surfaces as a background refresh
      // rather than a blanked switcher and a "Loading organizations…" flash.
      queueMicrotask(() => {
        if (!mounted) return;
        setError(null);
        setRetrying(false);
        if (publishedSubject) {
          setFallback((previous) =>
            previous && previous.subject === publishedSubject && previous.organizations === published
              ? previous
              : { organizations: published, subject: publishedSubject }
          );
        }
      });
      return () => {
        mounted = false;
      };
    }
    const requestSubject = getSessionSubject();
    const requestAuthGeneration = authGeneration;
    // Manual retry forces a fresh fetch rather than adopting the TTL cache or a
    // non-forced in-flight request, matching AuthGuard and OrganizationEntryGuard.
    void getOrganizationList({ force: retryNonce > 0 })
      .then((resolvedOrganizations) => {
        if (mounted && requestSubject && getSessionSubject() === requestSubject) {
          setFallback({ organizations: resolvedOrganizations, subject: requestSubject });
          setError(null);
        }
        if (mounted) setRetrying(false);
      })
      .catch((caught) => {
        if (!mounted) return;
        setRetrying(false);
        if (requestSubject !== getSessionSubject() || requestAuthGeneration !== getAuthGeneration()) {
          setError(null);
          return;
        }
        // Resolve same-identity failures to a terminal state so the switcher
        // cannot remain on "Loading organizations…" without a Retry affordance.
        // Identity transitions notify this hook separately and must never
        // surface the superseded request as a failure for the new account.
        setError(caught instanceof Error ? caught : new Error('Unable to load organizations'));
      });
    return () => {
      mounted = false;
    };
  }, [authGeneration, hasPublishedList, retryNonce]);

  const currentSubject = getSessionSubject();
  const organizations = publishedOrganizations ?? (fallback?.subject === currentSubject ? fallback.organizations : null);

  // Loading is derived, not latched: we are loading whenever no list is
  // available and no failure has been surfaced.
  const loading = organizations === null && error === null;

  return { organizations, loading, error, retry, retrying };
};
